import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import Topbar from "../components/Topbar";
import useLang from "../hooks/useLang";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ArrowLeft, ArrowRight, Share, Trash, CheckCircle, Sparkle, PaperPlaneTilt, BookOpen, Question, NotePencil, ChatCircleDots, Link as LinkIcon, Microphone } from "@phosphor-icons/react";
import CloneAvatar from "../components/CloneAvatar";
import AvatarUploader from "../components/AvatarUploader";
import PersonalityRadar from "../components/PersonalityRadar";
import VoiceChat from "../components/VoiceChat";
import { toast } from "sonner";

function ProfileSummary({ clone, stats, linkedClone, personality }) {
  const { t } = useLang();
  const categoryLabel = clone.category === "personal" ? t("categoryPersonal") : t("categoryWork");
  return (
    <aside className="lg:sticky lg:top-8 space-y-8" data-testid="profile-summary-panel">
      <div>
        <div className="flex items-center gap-3 mb-4">
          <CloneAvatar clone={clone} size={56} />
          <div>
            <div className="overline">/// {clone.industry}</div>
            <h2 className="font-heading text-xl font-medium tracking-tight">{clone.role_name}</h2>
          </div>
        </div>
        <div className="mb-3">
          <span className="tag" data-testid="clone-category-tag">{categoryLabel}</span>
        </div>
        <p className="text-sm text-zinc-600 leading-relaxed">{clone.responsibilities}</p>
      </div>

      {linkedClone && (
        <div className="border-t border-zinc-200 pt-6" data-testid="linked-clone-info">
          <div className="overline mb-3">/// {t("unifiedClone")}</div>
          <div className="flex items-center gap-2 mb-2">
            <CloneAvatar clone={linkedClone} size={32} />
            <div>
              <p className="text-sm font-medium">{linkedClone.role_name}</p>
              <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider">
                {linkedClone.category === "personal" ? t("categoryPersonal") : t("categoryWork")}
              </p>
            </div>
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed">{t("unifiedNote")}</p>
        </div>
      )}

      <div className="border-t border-zinc-200 pt-6">
        <div className="overline mb-3">/// {t("totalAnswers")}</div>
        <div className="font-heading text-5xl tracking-tight font-light" data-testid="stats-answer-count">
          {stats?.answer_count ?? 0}
        </div>
      </div>

      {personality?.ready && personality.dimensions?.length > 0 && (
        <div className="border-t border-zinc-200 pt-6" data-testid="personality-section">
          <div className="overline mb-3">/// {t("personalityTitle")}</div>
          <PersonalityRadar dimensions={personality.dimensions} size={220} />
          {personality.summary && (
            <p className="text-xs text-zinc-500 italic mt-3 leading-relaxed">&ldquo;{personality.summary}&rdquo;</p>
          )}
        </div>
      )}

      {stats?.topics?.length > 0 && (
        <div className="border-t border-zinc-200 pt-6">
          <div className="overline mb-3">/// {t("topicsCovered")}</div>
          <div className="flex flex-wrap gap-1.5" data-testid="topics-list">
            {stats.topics.slice(0, 20).map((tp) => (
              <span key={tp.name} className="tag">
                {tp.name} · {tp.count}
              </span>
            ))}
          </div>
        </div>
      )}

      {stats?.scenarios?.length > 0 && (
        <div className="border-t border-zinc-200 pt-6">
          <div className="overline mb-3">/// {t("scenarioTypes")}</div>
          <div className="flex flex-wrap gap-1.5">
            {stats.scenarios.map((sc) => (
              <span key={sc.name} className="tag">
                {sc.name} · {sc.count}
              </span>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}

function QuestionsPanel({ clone, mode, onAnswered, reloadStats }) {
  const { t } = useLang();
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    setGenerating(true);
    setQuestions([]);
    setIdx(0);
    setAnswer("");
    setSelectedOption(null);
    try {
      const endpoint =
        mode === "setup"
          ? `/clones/${clone.id}/calibrate`
          : `/clones/${clone.id}/train`;
      const body = mode === "setup" ? {} : { count: 1 };
      const { data } = await api.post(endpoint, body);
      setQuestions(data.questions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    const currentQ = questions[idx];
    if (!currentQ) return;
    const finalAnswer = selectedOption !== null ? currentQ.options[selectedOption] : answer.trim();
    if (!finalAnswer) return;
    setSaving(true);
    try {
      await api.post(`/clones/${clone.id}/answers`, {
        question: currentQ.question,
        answer: finalAnswer,
        source: mode === "setup" ? "calibration" : "training",
        was_preset: selectedOption !== null,
      });
      onAnswered?.();
      reloadStats?.();
      setAnswer("");
      setSelectedOption(null);
      if (idx + 1 < questions.length) {
        setIdx(idx + 1);
      } else {
        toast.success(t("sessionComplete"));
        setQuestions([]);
        setIdx(0);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
    } finally {
      setSaving(false);
    }
  };

  const skip = () => {
    setSelectedOption(null);
    if (idx + 1 < questions.length) {
      setIdx(idx + 1);
      setAnswer("");
    } else {
      setQuestions([]);
      setIdx(0);
    }
  };

  const title = mode === "setup" ? t("calibrationTitle") : t("trainingTitle");
  const subtitle = mode === "setup" ? t("calibrationSubtitle") : t("trainingSubtitle");

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-3">/// {mode === "setup" ? "Calibration" : "Training"}</div>
        <h2 className="font-heading text-3xl tracking-tight mb-2">{title}</h2>
        <p className="text-zinc-500 text-sm">{subtitle}</p>
      </div>

      {questions.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center">
          <Sparkle size={32} className="mx-auto mb-4 text-zinc-400" weight="light" />
          <p className="text-zinc-500 text-sm mb-6">
            {mode === "setup" ? t("calibrationSubtitle") : t("trainingSubtitle")}
          </p>
          <Button
            onClick={generate}
            disabled={generating}
            data-testid={mode === "setup" ? "calibrate-generate-btn" : "train-generate-btn"}
            className="bg-zinc-950 hover:bg-zinc-800 rounded-md gap-2"
          >
            <Sparkle size={16} weight="fill" />
            {generating ? t("loading") : mode === "training" ? t("todayQuestion") : t("generateQuestions")}
          </Button>
        </div>
      ) : (
        <div className="animate-fade-up space-y-6">
          {questions.length > 1 && (
            <div className="flex items-center justify-between">
              <span className="overline" data-testid="question-progress">
                /// {t("question")} {idx + 1} {t("of")} {questions.length}
              </span>
              <div className="flex gap-1">
                {questions.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1 w-6 rounded-full ${i < idx ? "bg-zinc-950" : i === idx ? "bg-zinc-500" : "bg-zinc-200"}`}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="border border-zinc-200 rounded-md p-6 lg:p-8 bg-zinc-50/50">
            <p className="font-heading text-xl sm:text-2xl tracking-tight leading-relaxed" data-testid="current-question">
              {questions[idx]?.question || questions[idx]}
            </p>
          </div>

          {questions[idx]?.options?.length > 0 && (
            <div className="space-y-3">
              <Label className="overline">{t("presetOption")}</Label>
              <div className="space-y-2">
                {questions[idx].options.map((opt, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setSelectedOption(i); setAnswer(""); }}
                    data-testid={`option-btn-${i}`}
                    className={`w-full text-left p-4 rounded-md border transition-all text-sm leading-relaxed ${
                      selectedOption === i
                        ? "border-zinc-950 bg-zinc-950 text-white"
                        : "border-zinc-200 bg-white hover:border-zinc-400 text-zinc-900"
                    }`}
                  >
                    <span className={`font-mono text-xs mr-3 ${selectedOption === i ? "text-zinc-300" : "text-zinc-400"}`}>
                      {String.fromCharCode(65 + i)}
                    </span>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="overline">{questions[idx]?.options?.length > 0 ? t("orCustomAnswer") : t("yourAnswer")}</Label>
            <Textarea
              value={answer}
              onChange={(e) => { setAnswer(e.target.value); if (e.target.value) setSelectedOption(null); }}
              rows={4}
              data-testid="answer-textarea"
              className="rounded-md border-zinc-200 text-base min-h-[100px]"
              placeholder={questions[idx]?.options?.length > 0 ? t("customAnswerPh") : "..."}
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              onClick={skip}
              data-testid="skip-btn"
              className="text-zinc-500 hover:text-zinc-900"
            >
              {t("skipQuestion")}
            </Button>
            <Button
              onClick={save}
              disabled={saving || (selectedOption === null && !answer.trim())}
              data-testid="save-answer-btn"
              className="bg-zinc-950 hover:bg-zinc-800 rounded-md gap-2 h-11 px-6"
            >
              {saving ? t("loading") : t("saveAnswer")}
              <ArrowRight size={16} />
            </Button>
          </div>

          {mode === "training" && questions.length === 1 && (
            <div className="pt-4 border-t border-zinc-100 text-center">
              <button
                type="button"
                onClick={generate}
                disabled={generating}
                data-testid="another-question-btn"
                className="text-sm text-zinc-500 hover:text-zinc-950 underline underline-offset-4"
              >
                {generating ? t("loading") : t("oneMoreQuestion")} →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChatPanel({ clone, disabled, user }) {
  const { t } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const consentKey = `consent_clone_${clone.id}_${user?.id || "anon"}`;
  const [consented, setConsented] = useState(() => localStorage.getItem(consentKey) === "1");
  const [showConsent, setShowConsent] = useState(false);

  const grantConsent = () => {
    localStorage.setItem(consentKey, "1");
    setConsented(true);
    setShowConsent(false);
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    if (!consented) {
      setShowConsent(true);
      return;
    }
    const q = input.trim();
    setInput("");
    const newMsgs = [...messages, { role: "user", text: q }];
    setMessages(newMsgs);
    setLoading(true);
    try {
      const { data } = await api.post(`/clones/${clone.id}/chat`, { question: q });
      setMessages([...newMsgs, { role: "clone", text: data.response, role_name: data.role_name }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
      setMessages(newMsgs);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-[500px]">
      <Dialog open={showConsent} onOpenChange={setShowConsent}>
        <DialogContent className="rounded-md border-zinc-200 max-w-md" data-testid="consent-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">{t("consentTitle")}</DialogTitle>
            <DialogDescription className="text-zinc-600 leading-relaxed pt-3">
              {t("consentBody")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setShowConsent(false)} data-testid="consent-decline-btn">
              {t("consentDecline")}
            </Button>
            <Button onClick={grantConsent} data-testid="consent-accept-btn" className="bg-zinc-950 hover:bg-zinc-800 rounded-md">
              {t("consentAccept")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div>
        <div className="overline mb-3">/// {t("cloneMode")}</div>
        <h2 className="font-heading text-3xl tracking-tight mb-2">{t("askQuestion")}</h2>
        <p className="text-zinc-500 text-sm">{t("respondingAs")} <span className="font-medium text-zinc-900">{clone.role_name}</span></p>
      </div>

      {!disabled && (
        <div className="mt-6">
          <div className="overline mb-2">/// {t("talkToMe")}</div>
          <VoiceChat
            clone={clone}
            disabled={disabled}
            onExchange={({ transcript, response }) => {
              setMessages((prev) => [...prev,
                { role: "user", text: transcript },
                { role: "clone", text: response, role_name: clone.role_name },
              ]);
            }}
          />
        </div>
      )}

      <div className="flex-1 space-y-4 my-8 min-h-[300px]">
        {disabled ? (
          <div className="border border-dashed border-zinc-300 rounded-md p-8 text-center">
            <p className="text-zinc-500 text-sm">{t("noAnswersYet")}</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-md p-8 text-center">
            <p className="text-zinc-500 text-sm">{t("askTheClone")}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[85%]">
                  {m.role === "clone" && (
                    <div className="overline mb-1.5 text-zinc-400">
                      /// {t("respondingAs")} {m.role_name}
                    </div>
                  )}
                  <div
                    className={`px-5 py-4 rounded-md whitespace-pre-wrap text-[15px] leading-relaxed ${
                      m.role === "user"
                        ? "bg-zinc-950 text-white rounded-br-sm"
                        : "bg-white border border-zinc-200 text-zinc-900 rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-zinc-200 px-5 py-4 rounded-md text-zinc-500 text-sm flex items-center gap-2">
                  <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-zinc-950" />
                  <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-zinc-950" style={{ animationDelay: "0.2s" }} />
                  <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-zinc-950" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <form onSubmit={send} className="flex items-center gap-2 border-t border-zinc-200 pt-6">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("askTheClone")}
          disabled={disabled || loading}
          data-testid="chat-input"
          className="rounded-md border-zinc-200 h-12 text-base"
        />
        <Button
          type="submit"
          disabled={disabled || loading || !input.trim()}
          data-testid="chat-send-btn"
          className="h-12 px-5 bg-zinc-950 hover:bg-zinc-800 rounded-md gap-2"
        >
          <PaperPlaneTilt size={16} weight="fill" />
        </Button>
      </form>
    </div>
  );
}

function DiaryPanel({ clone, reloadStats }) {
  const { t } = useLang();
  const [text, setText] = useState("");
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/clones/${clone.id}/diary`);
      setEntries(data);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { load(); }, [clone.id]);

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/clones/${clone.id}/diary`, { text: text.trim() });
      setText("");
      toast.success(t("saved"));
      load();
      reloadStats?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-3">/// Diary</div>
        <h2 className="font-heading text-3xl tracking-tight mb-2">{t("diaryTitle")}</h2>
        <p className="text-zinc-500 text-sm">{t("diarySubtitle")}</p>
      </div>
      <div className="border border-zinc-200 rounded-md p-6 bg-zinc-50/30 space-y-4">
        <p className="font-heading text-xl tracking-tight">Cosa è successo oggi e come l&apos;hai gestito?</p>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          placeholder={t("diaryPlaceholder")}
          data-testid="diary-textarea"
          className="rounded-md border-zinc-200 text-base min-h-[140px] bg-white"
        />
        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={saving || !text.trim()}
            data-testid="diary-save-btn"
            className="bg-zinc-950 hover:bg-zinc-800 rounded-md h-11 px-6 gap-2"
          >
            <NotePencil size={16} /> {saving ? t("loading") : t("saveDiary")}
          </Button>
        </div>
      </div>
      <div>
        <div className="overline mb-4">/// Storico</div>
        {entries.length === 0 ? (
          <p className="text-sm text-zinc-500">{t("noDiaryEntries")}</p>
        ) : (
          <div className="space-y-3" data-testid="diary-entries">
            {entries.map((e) => (
              <div key={e.id} className="border border-zinc-200 rounded-md p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono text-zinc-500">{(e.created_at || "").slice(0, 16).replace("T", " · ")}</span>
                  {e.topic && <span className="tag">{e.topic}</span>}
                </div>
                <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{e.answer}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FaqPanel({ clone }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/clones/${clone.id}/faq`);
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, [clone.id]);

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-3">/// FAQ</div>
        <h2 className="font-heading text-3xl tracking-tight mb-2">{t("faqTitle")}</h2>
        <p className="text-zinc-500 text-sm">{t("faqSubtitle")}</p>
      </div>
      {loading ? (
        <p className="text-sm text-zinc-500 font-mono">{t("loading")}</p>
      ) : !data || data.faqs.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center">
          <ChatCircleDots size={32} className="mx-auto mb-4 text-zinc-400" weight="light" />
          <p className="text-sm text-zinc-500">{t("noFaqYet")}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="border border-zinc-200 rounded-md p-4">
              <div className="overline mb-1">/// Totali</div>
              <div className="font-heading text-3xl font-light">{data.total_questions}</div>
            </div>
            <div className="border border-zinc-200 rounded-md p-4">
              <div className="overline mb-1">/// Uniche</div>
              <div className="font-heading text-3xl font-light">{data.unique_questions}</div>
            </div>
          </div>
          <div className="space-y-3" data-testid="faq-list">
            {data.faqs.map((f, i) => (
              <div key={i} className="border border-zinc-200 rounded-md p-5">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <p className="font-medium text-zinc-900 flex-1">{f.question}</p>
                  <span className="tag shrink-0">{f.count}× {t("asked")}</span>
                </div>
                <p className="text-xs font-mono text-zinc-500">{t("lastAsked")}: {(f.last_asked || "").slice(0, 16).replace("T", " · ")}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function LinkCloneDialog({ clone, available, linkedClone, onUpdate }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(linkedClone?.id || "none");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSelected(linkedClone?.id || "none"); }, [linkedClone]);

  const save = async () => {
    setSaving(true);
    try {
      const newLink = selected === "none" ? "" : selected;
      const { data } = await api.put(`/clones/${clone.id}`, { linked_clone_id: newLink });
      onUpdate?.(data);
      toast.success(t("saved"));
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
    } finally {
      setSaving(false);
    }
  };

  const unlink = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/clones/${clone.id}`, { linked_clone_id: "" });
      onUpdate?.(data);
      toast.success(t("unlink"));
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
    } finally {
      setSaving(false);
    }
  };

  const hasOptions = available.length > 0 || linkedClone;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" className={`rounded-md gap-2 border bg-white hover:bg-zinc-50 ${linkedClone ? "border-zinc-950 text-zinc-950" : "border-zinc-200"}`} data-testid="link-clone-btn">
          <LinkIcon size={16} weight={linkedClone ? "bold" : "regular"} />
          {linkedClone ? t("linkedWith") : t("linkClone")}
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-md border-zinc-200 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">{t("unifiedClone")}</DialogTitle>
          <DialogDescription className="text-zinc-500">{t("unifiedNote")}</DialogDescription>
        </DialogHeader>
        {!hasOptions ? (
          <p className="text-sm text-zinc-500 py-4">{t("noLinkableClones")}</p>
        ) : (
          <div className="space-y-4 py-4">
            <Label className="overline">{t("linkedClone")}</Label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="link-select" className="rounded-md border-zinc-200 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessuno —</SelectItem>
                {linkedClone && (
                  <SelectItem value={linkedClone.id}>
                    {linkedClone.avatar_emoji} {linkedClone.role_name} <span className="text-xs text-zinc-500 ml-1">· attuale</span>
                  </SelectItem>
                )}
                {available.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.avatar_emoji} {c.role_name} <span className="text-xs text-zinc-500 ml-1">· {c.category}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <DialogFooter className="gap-2">
          {linkedClone && (
            <Button variant="ghost" onClick={unlink} disabled={saving} data-testid="unlink-btn" className="text-red-600 hover:text-red-700 mr-auto">
              {t("unlink")}
            </Button>
          )}
          <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
          <Button onClick={save} disabled={saving || !hasOptions} data-testid="link-save-btn" className="bg-zinc-950 hover:bg-zinc-800 rounded-md">
            {saving ? t("loading") : t("save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShareDialog({ clone, onUpdate }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [visibility, setVisibility] = useState(clone.visibility || "private");
  const [emails, setEmails] = useState((clone.allowed_emails || []).join(", "));
  const [organization, setOrganization] = useState(clone.organization || "");
  const [saving, setSaving] = useState(false);

  const publicLink = `${window.location.origin}/public/${clone.id}`;

  const save = async () => {
    setSaving(true);
    try {
      const allowed_emails = emails
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean);
      const { data } = await api.patch(`/clones/${clone.id}/share`, {
        visibility,
        allowed_emails,
        organization: organization || null,
      });
      onUpdate?.(data);
      toast.success("Impostazioni condivisione aggiornate");
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(publicLink);
    toast.success(t("linkCopied"));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="secondary"
          className="rounded-md gap-2 border border-zinc-200 bg-white hover:bg-zinc-50"
          data-testid="share-btn"
        >
          <Share size={16} />
          {t("share")}
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-md border-zinc-200 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">{t("shareTitle")}</DialogTitle>
          <DialogDescription className="text-zinc-500">{t("shareSubtitle")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-4">
          <div className="space-y-2">
            <Label className="overline">Visibility</Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger data-testid="share-visibility-select" className="rounded-md border-zinc-200 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private">{t("visibilityPrivate")}</SelectItem>
                <SelectItem value="specific">{t("visibilitySpecific")}</SelectItem>
                <SelectItem value="public">{t("visibilityPublic")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {visibility === "specific" && (
            <>
              <div className="space-y-2">
                <Label className="overline">{t("allowedEmails")}</Label>
                <Textarea
                  value={emails}
                  onChange={(e) => setEmails(e.target.value)}
                  placeholder={t("allowedEmailsPh")}
                  data-testid="share-emails-input"
                  className="rounded-md border-zinc-200 min-h-[80px]"
                />
              </div>
              <div className="space-y-2">
                <Label className="overline">{t("organization")}</Label>
                <Input
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  placeholder={t("organizationPh")}
                  data-testid="share-org-input"
                  className="rounded-md border-zinc-200 h-11"
                />
              </div>
            </>
          )}
          {visibility === "public" && (
            <div className="space-y-2">
              <Label className="overline">{t("shareLink")}</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={publicLink}
                  data-testid="public-link-input"
                  className="rounded-md border-zinc-200 h-11 font-mono text-xs"
                />
                <Button
                  type="button"
                  onClick={copyLink}
                  data-testid="copy-link-btn"
                  className="h-11 bg-zinc-950 hover:bg-zinc-800 rounded-md"
                >
                  {t("copyLink")}
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t("cancel")}
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="share-save-btn"
            className="bg-zinc-950 hover:bg-zinc-800 rounded-md"
          >
            {saving ? t("loading") : t("save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CloneDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("mode") || "setup";
  const [tab, setTab] = useState(initialTab);
  const [clone, setClone] = useState(null);
  const [stats, setStats] = useState(null);
  const [linkedClone, setLinkedClone] = useState(null);
  const [availableToLink, setAvailableToLink] = useState([]);
  const [personality, setPersonality] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadClone = async () => {
    try {
      const { data } = await api.get(`/clones/${id}`);
      setClone(data);
      // Load linked clone info if any
      if (data.linked_clone_id) {
        try {
          const linked = await api.get(`/clones/${data.linked_clone_id}`);
          setLinkedClone(linked.data);
        } catch { setLinkedClone(null); }
      } else {
        setLinkedClone(null);
      }
      // Load candidates to link (owner-only)
      if (data.is_owner) {
        try {
          const all = await api.get("/clones");
          const opposite = data.category === "personal" ? "work" : "personal";
          setAvailableToLink(all.data.filter((c) => c.id !== data.id && c.category === opposite && !c.linked_clone_id));
        } catch { setAvailableToLink([]); }
      }
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      else if (err.response?.status === 403) {
        toast.error("Accesso negato");
        navigate("/");
      } else {
        toast.error(t("errorGeneric"));
      }
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get(`/clones/${id}/stats`);
      setStats(data);
    } catch (_e) { /* ignore */ }
    // Personality can take 15-30s; run in background
    (async () => {
      try {
        const { data } = await api.get(`/clones/${id}/personality`);
        setPersonality(data);
      } catch (_e) { /* ignore */ }
    })();
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadClone();
      await loadStats();
      setLoading(false);
    })();
  // eslint-disable-next-line
  }, [id]);

  const isOwner = clone?.is_owner;
  const canChat = (stats?.answer_count ?? 0) > 0;

  const deleteClone = async () => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/clones/${id}`);
      toast.success(t("delete"));
      navigate("/");
    } catch (err) {
      toast.error(t("errorGeneric"));
    }
  };

  if (loading || !clone) {
    return (
      <div className="min-h-screen bg-white">
        <Topbar />
        <div className="p-12 text-zinc-500 text-sm font-mono">{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <button
            onClick={() => navigate("/")}
            className="overline flex items-center gap-2 hover:text-zinc-900 transition-colors"
            data-testid="detail-back-btn"
          >
            <ArrowLeft size={14} /> {t("back")}
          </button>
          {isOwner && (
            <div className="flex items-center gap-2">
              <LinkCloneDialog clone={clone} available={availableToLink} linkedClone={linkedClone} onUpdate={(c) => { setClone(c); loadClone(); }} />
              <ShareDialog clone={clone} onUpdate={setClone} />
              <Button
                variant="ghost"
                onClick={deleteClone}
                data-testid="delete-clone-btn"
                className="text-zinc-500 hover:text-red-600 gap-2"
              >
                <Trash size={16} /> {t("delete")}
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <section className="lg:col-span-8">
            <Tabs value={tab} onValueChange={(v) => { setTab(v); setParams({ mode: v }); }}>
              <TabsList className="bg-transparent border-b border-zinc-200 rounded-none w-full justify-start gap-1 p-0 h-auto mb-8 overflow-x-auto">
                {isOwner && (
                  <>
                    <TabsTrigger
                      value="setup"
                      data-testid="tab-setup"
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-950 data-[state=active]:bg-transparent px-4 py-3 text-sm font-mono uppercase tracking-wider data-[state=active]:shadow-none"
                    >
                      {t("setup")}
                    </TabsTrigger>
                    <TabsTrigger
                      value="training"
                      data-testid="tab-training"
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-950 data-[state=active]:bg-transparent px-4 py-3 text-sm font-mono uppercase tracking-wider data-[state=active]:shadow-none"
                    >
                      {t("training")}
                    </TabsTrigger>
                    <TabsTrigger
                      value="diary"
                      data-testid="tab-diary"
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-950 data-[state=active]:bg-transparent px-4 py-3 text-sm font-mono uppercase tracking-wider data-[state=active]:shadow-none"
                    >
                      {t("diary")}
                    </TabsTrigger>
                    <TabsTrigger
                      value="faq"
                      data-testid="tab-faq"
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-950 data-[state=active]:bg-transparent px-4 py-3 text-sm font-mono uppercase tracking-wider data-[state=active]:shadow-none"
                    >
                      {t("faq")}
                    </TabsTrigger>
                  </>
                )}
                <TabsTrigger
                  value="clone"
                  data-testid="tab-clone"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-950 data-[state=active]:bg-transparent px-4 py-3 text-sm font-mono uppercase tracking-wider data-[state=active]:shadow-none"
                >
                  {t("clone")}
                </TabsTrigger>
              </TabsList>

              {isOwner && (
                <TabsContent value="setup" className="mt-0 space-y-6">
                  <AvatarUploader clone={clone} onChange={setClone} />
                  <QuestionsPanel
                    clone={clone}
                    mode="setup"
                    reloadStats={loadStats}
                  />
                </TabsContent>
              )}
              {isOwner && (
                <TabsContent value="training" className="mt-0">
                  <QuestionsPanel
                    clone={clone}
                    mode="training"
                    reloadStats={loadStats}
                  />
                </TabsContent>
              )}
              {isOwner && (
                <TabsContent value="diary" className="mt-0">
                  <DiaryPanel clone={clone} reloadStats={loadStats} />
                </TabsContent>
              )}
              {isOwner && (
                <TabsContent value="faq" className="mt-0">
                  <FaqPanel clone={clone} />
                </TabsContent>
              )}
              <TabsContent value="clone" className="mt-0">
                <ChatPanel clone={clone} disabled={!canChat} user={user} />
              </TabsContent>
            </Tabs>
          </section>

          <section className="lg:col-span-4">
            <ProfileSummary clone={clone} stats={stats} linkedClone={linkedClone} personality={personality} />
          </section>
        </div>
      </main>
    </div>
  );
}
